import { useIsMobileLayout } from '@psl/components/styles/responsive';
import { FontConfigContext } from '@psl/components/typography/config';
import { FC, memo, useContext, useMemo } from 'react';
import {
  Text as NativeText,
  TextInput as NativeTextInput,
  StyleProp,
  TextInputProps,
  TextProps,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { DefaultTheme, useTheme } from 'styled-components';
import { useIsDarkMode } from '../../appComponents/UserInterfaceIdiomContext';
import { useResponsiveContext } from '../../functions/responsive/ResponsiveContext';
import { FontFamily } from '../styles/Colors';
import { ParagraphClamped, ParagraphClampedProps } from './ParagraphClamped';

export namespace Typography {
  export type ResponsiveClass = 'mobile' | 'desktop';
  export type FontVariantClass = 'heading' | 'body' | 'button';
  export type FontSizeClass = 'small' | 'medium' | 'large';

  export type FontAttrs = {
    fontSize: number;
    lineHeight: number;
    letterSpacing?: number;
    fontWeight?: number;
  };

  export type DefaultFontAttrs = {
    fontFamily?: string;
    fontStyle?: TextStyle['fontStyle'];
    color?: string;
    fontScale?: number;
  };

  export type DefaultFontAttrsConfig = {
    [p in Typography.FontVariantClass]?: DefaultFontAttrs;
  };

  export type FontConfig = {
    defaults?: DefaultFontAttrsConfig;
  } & {
    [p in Typography.FontVariantClass]: {
      [p in FontSizeClass]:
        | FontAttrs
        | {
            [p in ResponsiveClass]: FontAttrs;
          };
    };
  };

  type FontFamilyType =
    | 'thin'
    | 'extraLight'
    | 'light'
    | 'regular'
    | 'medium'
    | 'semiBold'
    | 'bold'
    | 'extraBold'
    | 'black'
    | 'sequel100Black45'
    | 'sequel100Black65'
    | 'sequel100Black75';

  type TitleLevel = 1 | 2 | 3 | 4 | 5;

  type ColorValue = string | { dark: string; light: string };

  const TitleLevel2FontSizeMap: { [p in TitleLevel]: number } = {
    1: 19,
    2: 17,
    3: 15,
    4: 13,
    5: 11,
  };

  type DistributiveMap<T extends string> = T extends unknown
    ? {
        [p in T]?: true;
      }
    : never;

  type FontFamilyShortcut = DistributiveMap<FontFamilyType>;

  type FontFamilyShortcutMap = {
    [p in FontFamilyType]: { name: string; weight?: number };
  };

  const FontFamilyMap: FontFamilyShortcutMap = {
    thin: { name: FontFamily.Thin, weight: 100 },
    extraLight: { name: FontFamily.Light, weight: 200 },
    light: { name: FontFamily.Light, weight: 300 },
    regular: { name: FontFamily.Normal, weight: 400 },
    medium: { name: FontFamily.Medium, weight: 500 },
    semiBold: { name: FontFamily.SemiBold, weight: 600 },
    bold: { name: FontFamily.Bold, weight: 700 },
    extraBold: { name: FontFamily.Bold, weight: 800 },
    black: { name: FontFamily.Black, weight: 900 },
    sequel100Black45: { name: FontFamily.Sequel100Black45 },
    sequel100Black65: { name: FontFamily.Sequel100Black65 },
    sequel100Black75: { name: FontFamily.Sequel100Black75 },
  };

  const kFontFamilyShortcut = Object.keys(FontFamilyMap) as FontFamilyType[];

  const hasFamilyProp = (props: any) =>
    kFontFamilyShortcut.some(x => x in props);

  export type TextStyleProps = FontFamilyShortcut & {
    tag?: string;
    variant?: FontVariantClass;
    size?: FontSizeClass | number;
    family?: FontFamilyType;
    color?:
      | ColorValue
      | ((theme: DefaultTheme, isDarkMode: boolean) => ColorValue | undefined);
    lineHeight?: number;
    opacity?: number;
    italic?: boolean;
    margin?: number | 'auto';
    ml?: number | 'auto';
    mt?: number | 'auto';
    mr?: number | 'auto';
    mb?: number | 'auto';
    marginHorizontal?: number | 'auto';
    marginVertical?: number | 'auto';
    align?: TextStyle['textAlign'];
    alignSelf?: ViewStyle['alignSelf'];
    textTransform?: TextStyle['textTransform'];
    letterSpacing?: TextStyle['letterSpacing'];
    decorationLine?: TextStyle['textDecorationLine'];
    decorationStyle?: TextStyle['textDecorationStyle'];
    decorationColor?: ColorValue;
    textShadowColor?: TextStyle['textShadowColor'];
    textShadowOffset?: TextStyle['textShadowOffset'];
    textShadowRadius?: TextStyle['textShadowRadius'];
    withInheritedFontFamily?: boolean;
    withInheritedFontSize?: boolean;
    withInheritedTextColor?: boolean;
  };

  export type TextBaseProps = TextProps & TextStyleProps;

  const useTextBaseStyle = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tag,
    variant,
    size,
    alignSelf,
    color,
    align,
    italic,
    textTransform,
    style,
    lineHeight: lineHeight_,
    letterSpacing: letterSpacing_,
    opacity,
    margin,
    marginHorizontal,
    marginVertical,
    ml,
    mt,
    mr,
    mb,
    decorationLine: textDecorationLine,
    decorationStyle: textDecorationStyle,
    decorationColor,
    textShadowColor,
    textShadowOffset,
    textShadowRadius,
    withInheritedFontFamily,
    withInheritedFontSize,
    withInheritedTextColor,
    ...props
  }: TextBaseProps) => {
    const isMobile = useIsMobileLayout();
    const isDarkMode = useIsDarkMode();
    const theme = useTheme();

    const configContext = useContext(FontConfigContext);
    const responsiveClass: ResponsiveClass =
      configContext?.responsiveClass ?? (isMobile ? 'mobile' : 'desktop');

    const family: FontFamilyType | undefined = props.family as
      | FontFamilyType
      | undefined;

    let fontFamily: string | undefined;
    let fontWeight: number | undefined;
    let fontSize: number | undefined;
    let lineHeight = lineHeight_;
    let fontStyle: TextStyle['fontStyle'] | undefined;
    let letterSpacing = letterSpacing_;
    let fontScale = 1;

    const defaultConfig =
      variant != null ? configContext?.config?.defaults?.[variant] : undefined;

    fontFamily = family ? FontFamilyMap[family].name : undefined;
    fontWeight = family ? FontFamilyMap[family].weight : undefined;

    if (fontFamily == null) {
      if (variant == null) {
        for (const p of kFontFamilyShortcut) {
          if (p in props && (props as any)[p]) {
            fontFamily = FontFamilyMap[p].name;
            fontWeight = FontFamilyMap[p].weight;
            break;
          }
        }
        if (fontFamily == null) {
          fontFamily = FontFamilyMap.regular.name;
          fontWeight = FontFamilyMap.regular.weight;
        }
      } else {
        // apply the default font family
        if (defaultConfig?.fontFamily && withInheritedFontFamily) {
          fontFamily = defaultConfig.fontFamily;
        } else {
          fontFamily = FontFamilyMap.regular.name;
        }
        if (defaultConfig?.fontStyle && withInheritedFontFamily) {
          fontStyle = defaultConfig.fontStyle;
        }
        for (const p of kFontFamilyShortcut) {
          if (p in props && (props as any)[p]) {
            fontWeight = FontFamilyMap[p].weight;
            break;
          }
        }
      }
    }

    if (
      variant != null &&
      withInheritedFontFamily &&
      defaultConfig?.fontScale != null
    ) {
      fontScale = defaultConfig.fontScale;
    }

    if (typeof size === 'number') {
      fontSize = size;
    } else {
      if (size == null) {
        size = 'medium';
      }
      let fontAttrs: FontAttrs | undefined;
      const fontConfig =
        variant != null ? configContext?.config?.[variant]?.[size] : undefined;
      if (fontConfig) {
        if ('mobile' in fontConfig && 'desktop' in fontConfig) {
          fontAttrs = fontConfig[responsiveClass];
        } else {
          fontAttrs = fontConfig;
        }
        fontSize = fontAttrs.fontSize;
        if (lineHeight == null) {
          lineHeight = fontAttrs.lineHeight;
        }
        if (letterSpacing == null) {
          letterSpacing = fontAttrs.letterSpacing ?? letterSpacing_;
        }
        if (fontAttrs.fontWeight != null && fontWeight == null) {
          fontWeight = fontAttrs.fontWeight;
        }
      }
    }

    if (withInheritedFontSize) {
      if (fontSize != null) {
        fontSize *= fontScale;
      }
      if (lineHeight != null) {
        lineHeight *= fontScale;
      }
    }

    const textColorValue =
      (typeof color === 'function' ? color(theme, isDarkMode) : color) ??
      (withInheritedTextColor && defaultConfig?.color
        ? defaultConfig.color
        : isDarkMode
          ? 'white'
          : 'black');

    const textColor =
      typeof textColorValue === 'string'
        ? textColorValue
        : textColorValue[isDarkMode ? 'dark' : 'light'];

    const textDecorationColor =
      typeof decorationColor === 'string'
        ? decorationColor
        : decorationColor != null &&
            'dark' in decorationColor &&
            'light' in decorationColor
          ? decorationColor[isDarkMode ? 'dark' : 'light']
          : undefined;

    const styles = useMemo<StyleProp<TextStyle>>(() => {
      return [
        {
          alignSelf,
          textAlign: align,
          fontFamily,
          fontWeight:
            fontWeight != null ? (String(fontWeight) as any) : undefined,
          fontStyle: italic ? 'italic' : fontStyle,
          textTransform,
          fontSize,
          lineHeight,
          letterSpacing,
          color: textColor === 'inherit' ? undefined : textColor,
          textDecorationLine,
          textDecorationStyle,
          textDecorationColor,
          textShadowColor,
          textShadowOffset,
          textShadowRadius,
          opacity,
          marginLeft: ml ?? marginHorizontal ?? margin,
          marginRight: mr ?? marginHorizontal ?? margin,
          marginTop: mt ?? marginVertical ?? margin,
          marginBottom: mb ?? marginVertical ?? margin,
        },
        style,
      ];
    }, [
      alignSelf,
      align,
      fontFamily,
      fontStyle,
      fontWeight,
      italic,
      textTransform,
      fontSize,
      lineHeight,
      letterSpacing,
      textColor,
      textDecorationLine,
      textDecorationStyle,
      textDecorationColor,
      textShadowColor,
      textShadowOffset,
      textShadowRadius,
      opacity,
      ml,
      marginHorizontal,
      margin,
      mr,
      mt,
      marginVertical,
      mb,
      style,
    ]);

    return [styles, props] as const;
  };

  export const TextBase: FC<TextBaseProps> = memo(props => {
    const [styles, { children, ...textProps }] = useTextBaseStyle(props);

    return (
      <NativeText style={styles} {...textProps}>
        {children}
      </NativeText>
    );
  });

  export const Title: FC<TextBaseProps & { level?: TitleLevel }> = memo(
    ({ level = 4, ...props }) => {
      const { rpx } = useResponsiveContext();
      return (
        <TextBase
          size={rpx(TitleLevel2FontSizeMap[level])}
          semiBold={!hasFamilyProp(props) as any}
          {...props}
        />
      );
    },
  );

  export const Text: FC<TextBaseProps> = memo(props => {
    return <TextBase {...props} />;
  });

  export const Heading: FC<TextBaseProps> = memo(props => {
    return <TextBase variant="heading" {...props} />;
  });

  export const Body: FC<TextBaseProps> = memo(props => {
    return <TextBase variant="body" {...props} />;
  });

  export const Button: FC<TextBaseProps> = memo(props => {
    return <TextBase variant="button" {...props} />;
  });

  export const TextInput: FC<TextInputProps & TextBaseProps> = memo(props => {
    const [styles, { ...textProps }] = useTextBaseStyle(props);

    return <NativeTextInput style={styles} {...textProps} />;
  });

  export const Paragraph: FC<
    TextBaseProps & {
      ellipsis?: ParagraphClampedProps;
    }
  > = memo(({ ellipsis, ...props }) => {
    if (ellipsis?.numberOfLines != null && ellipsis?.numberOfLines > 0) {
      return <ParagraphClampImpl ellipsis={ellipsis} {...props} />;
    }
    return <TextBase {...props} />;
  });

  export const Label: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size ??= rpx(10);
      lineHeight ??= rpx(12);

      return (
        <TextBase
          size={size}
          lineHeight={lineHeight}
          bold
          color="black"
          {...props}
        />
      );
    },
  );

  export const H1: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size ??= rpx(26);
      lineHeight ??= rpx(31);

      return (
        <TextBase
          variant="heading"
          size={size}
          lineHeight={lineHeight}
          color="black"
          family="sequel100Black75"
          {...props}
        />
      );
    },
  );

  export const H2: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size ??= rpx(18);
      lineHeight ??= rpx(22);

      return (
        <TextBase
          variant="heading"
          size={size}
          lineHeight={lineHeight}
          bold
          color="black"
          {...props}
        />
      );
    },
  );

  export const H3: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size ??= rpx(14);
      lineHeight ??= rpx(22);

      return (
        <TextBase
          variant="heading"
          size={size}
          lineHeight={lineHeight}
          bold
          color="black"
          {...props}
        />
      );
    },
  );

  // legacy button text
  export const ButtonText: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size ??= rpx(14);
      lineHeight ??= rpx(22);

      return (
        <TextBase
          size={size}
          lineHeight={lineHeight}
          semiBold
          color="black"
          {...props}
        />
      );
    },
  );

  // legacy section header text
  export const SectionHeader: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size ??= rpx(14);
      lineHeight ??= rpx(22);

      return (
        <TextBase
          size={size}
          lineHeight={lineHeight}
          family="sequel100Black65"
          color="black"
          {...props}
        />
      );
    },
  );

  const ParagraphClampImpl: FC<
    TextBaseProps & {
      ellipsis: ParagraphClampedProps;
    }
  > = memo(({ ellipsis, size, lineHeight, ...props }) => {
    const { rpx } = useResponsiveContext();
    size ??= rpx(12);
    lineHeight ??= rpx(18);

    const [style, { children }] = useTextBaseStyle({
      ...props,
      size,
      lineHeight,
    });

    return (
      <ParagraphClamped {...ellipsis} textStyle={style}>
        {children}
      </ParagraphClamped>
    );
  });
}

export default Typography;
